<template>
  <div ref="wrapper"
       class="app-textarea-wrapper"
       :class="{ 'app-textarea-wrapper--auto-grow': autoGrow }"
       :data-replicated-value="mirrorValue">
    <textarea
      ref="textarea"
      class="form-control app-textarea"
      :class="[sizeClass, { 'app-textarea--auto-grow': autoGrow, 'app-textarea--no-resize': noResize && !autoGrow }]"
      :id="id"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :autofocus="autofocus"
      :rows="fixedRows"
      :value="localValue"
      v-on="forwardedListeners"
      @input="onInput"
      @change="onChange"
      @blur="onBlur"
    ></textarea>
  </div>
</template>

<script>
/**
 * Reemplazo de bootstrap-vue's b-form-textarea (con max-rows="N"): mismo look
 * (clases `form-control`) y misma API de uso (v-model, rows, max-rows, size,
 * lazy, etc.), pero con el auto-grow resuelto sin JS en el camino crítico.
 *
 * Por qué existe (segunda vuelta): la primera reescritura seguía midiendo
 * `el.scrollHeight` en cada tecla (aunque atenuado con throttle), y esa
 * lectura fuerza un reflow síncrono ("forced reflow") antes de poder pintar
 * el frame. En una PC de escritorio ese costo es imperceptible; en un
 * celular real de un vendedor (CPU mucho más lenta + teclado virtual) se
 * siente trabado — se confirmó en producción con Mensajería Interna.
 *
 * La solución: técnica de "espejo invisible" con CSS Grid (mismo patrón que
 * usan Slack/Discord/GitHub para sus textareas auto-crecientes). El texto
 * tipeado se refleja en un ::after invisible que ocupa la misma celda de
 * grid que el textarea real; el motor de layout del navegador crece la
 * celda solo, como parte de su ciclo normal de pintado — sin que JS lea
 * nunca una propiedad de layout en el medio de una tecla. `syncMetrics()`
 * (padding/borde/línea/tope, vía getComputedStyle) corre una sola vez al
 * montar y cuando cambian props de tamaño o el ancho del propio elemento
 * (ResizeObserver) — nunca durante el tipeo.
 */

export default {
  name: 'AppTextarea',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    rows: { type: [String, Number], default: 2 },
    maxRows: { type: [String, Number], default: null },
    size: { type: String, default: null }, // 'sm' | 'lg'
    lazy: { type: Boolean, default: false },
    noResize: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    id: { type: String, default: null },
    name: { type: String, default: null },
    placeholder: { type: String, default: null },
  },
  data() {
    return {
      localValue: this.value == null ? '' : String(this.value),
    }
  },
  computed: {
    minRows() {
      return Math.max(parseInt(this.rows, 10) || 2, 2)
    },
    maxRowsNumber() {
      const n = parseInt(this.maxRows, 10)
      return n && n > this.minRows ? n : null
    },
    autoGrow() {
      return !!this.maxRowsNumber
    },
    // Con auto-grow la altura la maneja el grid (vía el espejo), no el
    // atributo `rows` nativo del textarea.
    fixedRows() {
      return this.autoGrow ? null : this.minRows
    },
    sizeClass() {
      return this.size === 'sm' || this.size === 'lg' ? 'form-control-' + this.size : ''
    },
    // Todos los listeners del padre (@keydown, @focus, etc.) se reenvían tal
    // cual al textarea nativo, excepto input/change/blur: esos los maneja
    // este componente para poder implementar `lazy`.
    forwardedListeners() {
      // eslint-disable-next-line no-unused-vars
      const { input, change, blur, ...rest } = this.$listeners
      return rest
    },
    // Texto que ve el ::after invisible para calcular el alto. El espacio
    // final evita que un \n al final del texto "desaparezca" visualmente
    // (whitespace final se puede colapsar sin él). Solo se genera cuando
    // hay auto-grow: en los ~60 usos sin max-rows esto queda en null y Vue
    // ni siquiera escribe el atributo — cero costo extra para esos casos.
    mirrorValue() {
      return this.autoGrow ? this.localValue + ' ' : null
    },
  },
  watch: {
    value(newValue) {
      const str = newValue == null ? '' : String(newValue)
      if (str !== this.localValue) {
        this.localValue = str
      }
    },
    size() {
      this.$nextTick(this.syncMetrics)
    },
    rows() {
      this.$nextTick(this.syncMetrics)
    },
    maxRows() {
      this.$nextTick(this.syncMetrics)
    },
  },
  mounted() {
    this.syncMetrics()
    if (this.autoGrow && typeof ResizeObserver !== 'undefined') {
      // Recalcula si cambia el ancho disponible (rotación de pantalla,
      // sidebar que se abre/cierra, etc.) — evento raro, no atado al tipeo.
      this._resizeObserver = new ResizeObserver(() => this.syncMetrics())
      this._resizeObserver.observe(this.$refs.wrapper)
    }
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  },
  methods: {
    onInput(event) {
      this.localValue = event.target.value
      if (!this.lazy) {
        this.$emit('input', this.localValue)
      }
    },
    onChange(event) {
      this.localValue = event.target.value
      // en modo lazy, change/blur es el único momento en que se actualiza el v-model
      this.$emit('input', this.localValue)
      this.$emit('change', this.localValue)
    },
    onBlur(event) {
      if (this.lazy) {
        this.$emit('input', this.localValue)
      }
      this.$emit('blur', event)
    },
    // Mide padding/borde/tipografía reales del textarea (los define
    // Bootstrap vía .form-control/.form-control-sm/lg) para que el espejo
    // envuelva el texto exactamente igual, más el alto mínimo/máximo según
    // rows/maxRows. Es la única lectura de layout de todo el componente, y
    // corre solo acá — nunca en onInput.
    syncMetrics() {
      const wrapper = this.$refs.wrapper
      const el = this.$refs.textarea
      if (!this.autoGrow || !wrapper || !el) return

      const cs = getComputedStyle(el)
      const lineHeight = parseFloat(cs.lineHeight) || 20
      const border = parseFloat(cs.borderTopWidth || 0) + parseFloat(cs.borderBottomWidth || 0)
      const padding = parseFloat(cs.paddingTop || 0) + parseFloat(cs.paddingBottom || 0)
      const offset = border + padding

      const minHeight = Math.ceil(lineHeight * this.minRows + offset)
      const maxHeight = Math.ceil(lineHeight * this.maxRowsNumber + offset)

      const style = wrapper.style
      style.setProperty('--app-ta-font-size', cs.fontSize)
      style.setProperty('--app-ta-font-family', cs.fontFamily)
      style.setProperty('--app-ta-font-weight', cs.fontWeight)
      style.setProperty('--app-ta-line-height', cs.lineHeight)
      style.setProperty('--app-ta-pad-top', cs.paddingTop)
      style.setProperty('--app-ta-pad-right', cs.paddingRight)
      style.setProperty('--app-ta-pad-bottom', cs.paddingBottom)
      style.setProperty('--app-ta-pad-left', cs.paddingLeft)
      style.setProperty('--app-ta-border-top', cs.borderTopWidth)
      style.setProperty('--app-ta-border-right', cs.borderRightWidth)
      style.setProperty('--app-ta-border-bottom', cs.borderBottomWidth)
      style.setProperty('--app-ta-border-left', cs.borderLeftWidth)
      style.setProperty('--app-ta-min-height', minHeight + 'px')
      style.setProperty('--app-ta-max-height', maxHeight + 'px')
    },
    focus() {
      if (this.$refs.textarea) this.$refs.textarea.focus()
    },
    blur() {
      if (this.$refs.textarea) this.$refs.textarea.blur()
    },
  },
}
</script>

<style scoped>
/* Sin auto-grow (la mayoría de los usos): el wrapper queda fuera de la caja
   de layout por completo (display:contents), así que el textarea se
   comporta exactamente igual que si el div no existiera — cero riesgo de
   regresión visual para esos casos. */
.app-textarea-wrapper {
  display: contents;
}
.app-textarea-wrapper--auto-grow {
  display: grid;
  /* El textarea real dependía de .form-control{width:100%} para ocupar el
     ancho disponible dentro de layouts flex (ej. el composer de mensajes).
     Ahora que el item flex es este wrapper (no el textarea directamente),
     hay que repetir esa regla acá o el wrapper se encoge a su contenido. */
  width: 100%;
  max-height: var(--app-ta-max-height, none);
  overflow: hidden;
}
/* El espejo: mismo texto, mismo padding/borde/tipografía que el textarea
   real, apilado en la misma celda de grid (grid-area 1/1/2/2) — el navegador
   crece la celda solo según el contenido, sin que JS pregunte nada. */
.app-textarea-wrapper--auto-grow::after {
  content: attr(data-replicated-value);
  grid-area: 1 / 1 / 2 / 2;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  box-sizing: border-box;
  min-height: var(--app-ta-min-height, auto);
  max-height: var(--app-ta-max-height, none);
  overflow: hidden;
  padding-top: var(--app-ta-pad-top, 0);
  padding-right: var(--app-ta-pad-right, 0);
  padding-bottom: var(--app-ta-pad-bottom, 0);
  padding-left: var(--app-ta-pad-left, 0);
  border-top: var(--app-ta-border-top, 0) solid transparent;
  border-right: var(--app-ta-border-right, 0) solid transparent;
  border-bottom: var(--app-ta-border-bottom, 0) solid transparent;
  border-left: var(--app-ta-border-left, 0) solid transparent;
  font-size: var(--app-ta-font-size, inherit);
  font-family: var(--app-ta-font-family, inherit);
  font-weight: var(--app-ta-font-weight, inherit);
  line-height: var(--app-ta-line-height, inherit);
}
.app-textarea-wrapper--auto-grow > .app-textarea {
  grid-area: 1 / 1 / 2 / 2;
  height: 100%;
  /* max-height explícito además de height:100%: el track de grid se mide
     por el tamaño natural del espejo (::after), y height:100% se estira
     contra ESE track, no contra la caja ya recortada del wrapper — sin
     este tope directo, el textarea igual termina más alto que el wrapper. */
  max-height: var(--app-ta-max-height, none);
  /* Los elementos de formulario tienen min-height:auto por default en grid,
     que ignora el height:100% y no deja que el textarea sea más chico que
     su propio contenido — sin esto, crece igual que el espejo y nunca hay
     nada para scrollear aunque el wrapper sí esté topeado por max-height. */
  min-height: 0;
}
.app-textarea {
  /* Acota el costo de cualquier recálculo de layout al propio textarea en
     vez de a toda la página. */
  contain: layout style;
}
.app-textarea--auto-grow {
  /* overflow-y:scroll (no auto) a propósito: reserva el ancho del
     scrollbar siempre, así el textarea real y el espejo (que nunca tiene
     scrollbar) envuelven el texto con el mismo ancho disponible. */
  overflow-y: scroll;
  resize: none;
}
.app-textarea--no-resize {
  resize: none;
}
</style>
